import { localDB } from './pouchdb-config';
import authService from '../services/auth-service';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.organicgreenshub.com';

const orderService = {
  getAllOrders: async () => {
    try {
      const result = await localDB.allDocs({ include_docs: true });
      const orders = result.rows
        ?.map(row => row.doc)
        ?.filter(doc => doc.type === 'order') || [];
      
      console.log(`Found ${orders.length} orders`);
      return orders;
    } catch (error) {
      console.error('Error getting orders:', error);
      throw error;
    }
  },

  getOrderById: async (id) => {
    try {
      return await localDB.get(id);
    } catch (error) {
      console.error(`Error getting order with ID ${id}:`, error);
      throw error;
    }
  },

  findOrdersByCustomerId: async (customerId) => {
    try {
      console.log(`Finding orders for customer: ${customerId}`);
      
      // Check authentication
      const token = authService.getToken();
      if (!token) {
        throw new Error('Please log in to view your orders.');
      }
      
      // Use optimized backend endpoint
      const response = await fetch(`${API_URL}/api/orders/customer/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please log in to view your orders.');
        }
        if (response.status === 403) {
          throw new Error('Access denied.');
        }
        throw new Error('Failed to load orders. Please try again later.');
      }
      
      const orders = await response.json();
      console.log(`Found ${orders.length} orders for customer ${customerId}`);
      
      return orders;
      
    } catch (error) {
      console.error(`Error finding orders for customer ${customerId}:`, error);
      
      if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        throw new Error('Unable to connect to database. Please check your internet connection.');
      }
      if (error.message.includes('log in') || error.message.includes('Access denied')) {
        throw error;
      }
      throw new Error('Failed to load orders. Please try again later.');
    }
  },
  
  findOrdersByProductId: async (productId) => {
    try {
      // Get all orders
      const allOrders = await orderService.getAllOrders();
      
      // Filter orders that contain the specified product
      const ordersWithProduct = allOrders.filter(order => {
        if (!order.items || !Array.isArray(order.items)) return false;
        return order.items.some(item => item.productId === productId);
      });
      
      // Sort manually
      const sortedOrders = ordersWithProduct.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
      
      // Format the orders for display
      return sortedOrders.map(order => {
        // Find the specific item in the order
        const item = order.items.find(item => item.productId === productId);
        
        return {
          orderId: order._id,
          fname: order.customerName || "Customer",
          lname: "",
          createdDate: new Date(order.date).toLocaleDateString(),
          quantity: item ? item.quantity : 0,
          totalPrice: item ? (item.price * item.quantity) : 0
        };
      });
    } catch (error) {
      console.error(`Error finding orders for product ${productId}:`, error);
      throw error;
    }
  },
  
  findOrdersByStatus: async (status) => {
    try {
      const result = await localDB.allDocs({ include_docs: true });
      const orders = result.rows
        ?.map(row => row.doc)
        ?.filter(doc => doc.type === 'order' && doc.status === status) || [];
      
      return orders.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
    } catch (error) {
      console.error(`Error finding orders with status ${status}:`, error);
      throw error;
    }
  },
  
  findOrdersByDateRange: async (startDate, endDate) => {
    try {
      const result = await localDB.allDocs({ include_docs: true });
      const orders = result.rows
        ?.map(row => row.doc)
        ?.filter(doc => {
          if (doc.type !== 'order') return false;
          const orderDate = new Date(doc.date);
          return orderDate >= new Date(startDate) && orderDate <= new Date(endDate);
        }) || [];
      
      return orders.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
    } catch (error) {
      console.error(`Error finding orders in date range:`, error);
      throw error;
    }
  },
  
  // Add method to update order status
  updateOrderStatus: async (orderId, status, paymentId = null) => {
    try {
      const order = await localDB.get(orderId);
      
      // Update status and payment info
      order.status = status;
      if (paymentId) {
        order.paymentId = paymentId;
      }
      order.updatedAt = new Date().toISOString();
      
      // Set payment status if status is 'paid'
      if (status === 'paid') {
        order.paymentStatus = 'Paid';
      }
      
      // Save the updated order
      await localDB.put(order);
      
      return { success: true, order };
    } catch (error) {
      console.error(`Error updating order status for ${orderId}:`, error);
      throw error;
    }
  },
  
  // Add method to update entire order
  updateOrder: async (order) => {
    try {
      await localDB.put(order);
      return { success: true, order };
    } catch (error) {
      console.error(`Error updating order:`, error);
      throw error;
    }
  }
};

export default orderService;